import {
	INLINE_FLOW_PERSISTED_IDENTITY_FIELDS,
	type InlineFlow,
	inlineFlowSchema,
} from "@getuserfeedback/protocol/internal/inline-flow";
import type { Client, FlowRun, OpenFlowOptions } from "../client";
import { getInlineFlowClientBridge } from "./inline-flow-client-bridge";

export type { BundledDocsClientOptions } from "../client";
export { createBundledDocsClientOptions } from "../client";

/** Internal supplied-flow run using the normal Flow lifecycle. */
export type InlineFlowRun = Omit<FlowRun, "flowId" | "open"> & {
	open: (options?: Omit<OpenFlowOptions, "container">) => Promise<void>;
};

const ARRAY_INDEX_KEY_PATTERN = /^(0|[1-9]\d*)$/;

const invalidJsonSnapshot = (): never => {
	throw new Error("Inline flow must contain only JSON values.");
};

const toJsonSnapshot = (
	value: unknown,
	ancestors = new Set<object>(),
): unknown => {
	if (
		value === null ||
		typeof value === "string" ||
		typeof value === "boolean"
	) {
		return value;
	}
	if (typeof value === "number") {
		return Number.isFinite(value) ? value : invalidJsonSnapshot();
	}
	if (typeof value !== "object" || ancestors.has(value)) {
		return invalidJsonSnapshot();
	}

	ancestors.add(value);
	try {
		if (Array.isArray(value)) {
			if (Object.getPrototypeOf(value) !== Array.prototype) {
				return invalidJsonSnapshot();
			}
			const ownKeys = Reflect.ownKeys(value);
			if (
				ownKeys.length !== value.length + 1 ||
				ownKeys.some(
					(key) =>
						key !== "length" &&
						(typeof key !== "string" ||
							!ARRAY_INDEX_KEY_PATTERN.test(key) ||
							Number(key) >= value.length),
				)
			) {
				return invalidJsonSnapshot();
			}
			const snapshot: unknown[] = [];
			for (let index = 0; index < value.length; index += 1) {
				const descriptor = Object.getOwnPropertyDescriptor(
					value,
					String(index),
				);
				if (!descriptor?.enumerable || !("value" in descriptor)) {
					return invalidJsonSnapshot();
				}
				snapshot.push(toJsonSnapshot(descriptor.value, ancestors));
			}
			return snapshot;
		}

		const prototype = Object.getPrototypeOf(value);
		if (prototype !== Object.prototype && prototype !== null) {
			return invalidJsonSnapshot();
		}
		const snapshot: Record<string, unknown> = {};
		for (const key of Reflect.ownKeys(value)) {
			if (typeof key !== "string" || key === "__proto__") {
				return invalidJsonSnapshot();
			}
			const descriptor = Object.getOwnPropertyDescriptor(value, key);
			if (!descriptor?.enumerable || !("value" in descriptor)) {
				return invalidJsonSnapshot();
			}
			if (descriptor.value === undefined) {
				continue;
			}
			Object.defineProperty(snapshot, key, {
				configurable: true,
				enumerable: true,
				value: toJsonSnapshot(descriptor.value, ancestors),
				writable: true,
			});
		}
		return snapshot;
	} finally {
		ancestors.delete(value);
	}
};

const snapshotInlineFlowInput = (flow: unknown): Record<string, unknown> => {
	if (
		typeof flow !== "object" ||
		flow === null ||
		Array.isArray(flow) ||
		(Object.getPrototypeOf(flow) !== Object.prototype &&
			Object.getPrototypeOf(flow) !== null)
	) {
		return invalidJsonSnapshot();
	}
	return toJsonSnapshot(flow) as Record<string, unknown>;
};

const freezeJsonSnapshot = <Value>(value: Value): Value => {
	if (typeof value !== "object" || value === null) {
		return value;
	}
	for (const child of Object.values(value)) {
		freezeJsonSnapshot(child);
	}
	return Object.freeze(value);
};

const snapshotInlineFlow = (flow: InlineFlow): InlineFlow => {
	const snapshot = inlineFlowSchema.parse(snapshotInlineFlowInput(flow));
	const definition = snapshot.definition;
	if (
		typeof definition === "object" &&
		definition !== null &&
		!Array.isArray(definition)
	) {
		const field = INLINE_FLOW_PERSISTED_IDENTITY_FIELDS.find(
			(candidate) => candidate in definition,
		);
		if (field) {
			throw new Error(
				`Inline flow definitions cannot include persisted field "${field}".`,
			);
		}
	}
	return freezeJsonSnapshot(snapshot);
};

/** First-party supplied-flow entrypoint; intentionally absent from the public SDK root. */
export const createInlineFlow = (
	client: Client,
	flow: InlineFlow,
): InlineFlowRun =>
	getInlineFlowClientBridge(client).createInlineFlowRun(
		snapshotInlineFlow(flow),
	);

export type { InlineFlow };
